/**
 * useStageAdmin — stage management actions shared by the board's column
 * tools and the Settings → Pipeline Stages page. Every action reports
 * failures as toasts; the delete flow is a modal the caller renders.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { STAGES_KEY, useStageMutations } from '@/features/pipeline/api';
import type { Stage, StageKind } from '@/features/pipeline/types';
import { errorMessage, useToast } from '@/components/Toast';
import { DEFAULT_STAGE_COLOR } from './stage-colors';

export interface PendingDelete {
  stage: Stage;
  count: number;
}

/**
 * After a column is deleted its menu button is gone, so the modal's focus
 * restore lands on <body>. Hand focus to the destination stage's heading
 * (board `<h2 tabindex=-1>` / settings name input) once the list refetched.
 */
export function focusStageHeading(stageId: string | undefined): void {
  if (!stageId) return;
  const el = document.querySelector<HTMLElement>(`[data-stage-heading="${stageId}"]`);
  el?.focus({ preventScroll: false });
}

export function useStageAdmin(stages: Stage[]) {
  const { create, update, reorder, remove } = useStageMutations();
  const qc = useQueryClient();
  const toast = useToast();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const fail = useCallback(
    (title: string) => (err: unknown) => toast.push({ kind: 'error', title, message: errorMessage(err) }),
    [toast],
  );

  const rename = useCallback(
    (stage: Stage, name: string) => {
      const trimmed = name.trim();
      if (!trimmed || trimmed === stage.name) return;
      update.mutate({ id: stage.id, name: trimmed }, { onError: fail('Could not rename stage') });
    },
    [update, fail],
  );

  const recolor = useCallback(
    (stage: Stage, color: string) =>
      update.mutate({ id: stage.id, color }, { onError: fail('Could not change colour') }),
    [update, fail],
  );

  const setKind = useCallback(
    (stage: Stage, kind: StageKind) =>
      update.mutate({ id: stage.id, kind }, { onError: fail('Could not change stage type') }),
    [update, fail],
  );

  const reorderTo = useCallback(
    (orderedIds: string[]) => reorder.mutate(orderedIds, { onError: fail('Could not reorder stages') }),
    [reorder, fail],
  );

  const move = useCallback(
    (stage: Stage, dir: -1 | 1) => {
      const ids = stages.map((s) => s.id);
      const i = ids.indexOf(stage.id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j]!, ids[i]!];
      reorderTo(ids);
    },
    [stages, reorderTo],
  );

  const add = useCallback(
    (name: string, kind: StageKind = 'open', afterStageId?: string, onDone?: () => void) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      create.mutate(
        { name: trimmed, kind, color: DEFAULT_STAGE_COLOR, after_stage_id: afterStageId },
        {
          onSuccess: () => {
            toast.push({ kind: 'success', title: `Added “${trimmed}”` });
            onDone?.();
          },
          onError: fail('Could not add stage'),
        },
      );
    },
    [create, fail, toast],
  );

  const confirmDelete = useCallback(
    (moveTo: string | undefined) => {
      if (!pendingDelete) return;
      const { stage } = pendingDelete;
      // Where focus goes afterwards: the destination, else the neighbour.
      const i = stages.findIndex((s) => s.id === stage.id);
      const neighbour = stages[i + 1]?.id ?? stages[i - 1]?.id;
      const focusTarget = moveTo ?? neighbour;
      remove.mutate(
        { id: stage.id, move_to: moveTo },
        {
          onSuccess: (res) => {
            setPendingDelete(null);
            void qc.refetchQueries({ queryKey: STAGES_KEY }).then(() => {
              requestAnimationFrame(() => focusStageHeading(focusTarget));
            });
            if (res && res.warning === 'system_stage_removed') {
              toast.push({
                kind: 'info',
                title: `Deleted “${stage.name}”`,
                message: `Automation that targeted ${res.system_key ?? stage.system_key ?? 'this stage'} will now be skipped and logged.`,
                duration: 9000,
              });
            } else {
              toast.push({ kind: 'success', title: `Deleted “${stage.name}”` });
            }
          },
          onError: fail('Could not delete stage'),
        },
      );
    },
    [pendingDelete, remove, toast, fail, stages, qc],
  );

  return {
    rename,
    recolor,
    setKind,
    move,
    reorderTo,
    add,
    pendingDelete,
    requestDelete: (stage: Stage, count: number) => setPendingDelete({ stage, count }),
    cancelDelete: () => setPendingDelete(null),
    confirmDelete,
    deleting: remove.isPending,
    adding: create.isPending,
  };
}

export type StageAdmin = ReturnType<typeof useStageAdmin>;
